import React, { useRef, useEffect } from "react";

/**
 * CameraComponent
 *
 * This component accesses the user's front-facing camera and streams video
 * directly to a `<video>` element. On component unmount, the stream is properly
 * stopped to release camera resources.
 */
const CameraComponent = () => {
  const videoRef = useRef(null); // Ref to the <video> element

  useEffect(() => {
    // Define video constraints to prefer front-facing camera
    const constraints = {
      video: {
        facingMode: "user", // Use front camera on supported devices
      },
    };

    // Request access to the user's media devices (camera)
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          // Assign the media stream to the video element
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((error) => {
          console.error("Error accessing the camera", error);
        });
    }

    // Cleanup: stop all media tracks on unmount to release camera
    return () => {
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline // Ensures iOS devices don't go fullscreen
      preload="none" // Prevents preloading (performance)
      disablePictureInPicture // Disables PiP mode
      disableRemotePlayback // Prevents streaming to remote devices
    />
  );
};

export default CameraComponent;
